import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { Send, MessageSquare, Plus, X, Search, ArrowLeft } from 'lucide-react';
import VerificationGate from '@/components/verification/VerificationGate';
import { canDo } from '@/utils/verifications';
import { BACKEND_URL } from '@/config';
import VerificationBadges from '@/components/VerificationBadges';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

function formatTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return formatTime(d);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function getInitials(person) {
  if (!person) return '?';
  return `${(person.first_name || '')[0] || ''}${(person.last_name || '')[0] || ''}`.toUpperCase();
}

const TABS = [
  { key: 'all',      label: 'Tous' },
  { key: 'MISSION',  label: 'Missions' },
  { key: 'DIRECT',   label: 'Directs' },
];

export default function MessagingPage() {
  const { id: convIdParam } = useParams();
  const { user } = useAuth();
  const toast = useToast();

  const [conversations, setConversations]   = useState([]);
  const [activeConv, setActiveConv]         = useState(null);
  const [messages, setMessages]             = useState([]);
  const [newMsg, setNewMsg]                 = useState('');
  const [sending, setSending]               = useState(false);
  const [loading, setLoading]               = useState(true);
  const [activeTab, setActiveTab]           = useState('all');

  // New DM search panel
  const [showSearch, setShowSearch]         = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [searching, setSearching]           = useState(false);
  const [startingDM, setStartingDM]         = useState(false);

  const msgEndRef  = useRef(null);
  const searchRef  = useRef(null);
  const debounceRef = useRef(null);

  // ── Load conversations ──────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const params = activeTab !== 'all' ? { type: activeTab } : {};
      const { data } = await axios.get(`${API}/conversations`, { params });
      setConversations(data || []);
      if (!activeConv && convIdParam) {
        const found = (data || []).find(c => c.id === convIdParam);
        if (found) openConversation(found);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    loadConversations();
    const id = setInterval(loadConversations, 30000);
    return () => clearInterval(id);
  }, [loadConversations]);

  // ── Open conversation ───────────────────────────────────────────────────────
  const openConversation = async (conv) => {
    setActiveConv(conv);
    setShowSearch(false);
    try {
      const { data } = await axios.get(`${API}/conversations/${conv.id}`);
      setMessages(data.messages || []);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Erreur de chargement'); }
  };

  // ── Poll active conversation every 5s ──────────────────────────────────────
  useEffect(() => {
    if (!activeConv) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/conversations/${activeConv.id}`);
        const newMsgs = data.messages || [];
        setMessages(prev => {
          if (newMsgs.length > prev.length) {
            setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          }
          return newMsgs;
        });
      } catch { /* ignore */ }
    };
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [activeConv]);

  // ── Send message ────────────────────────────────────────────────────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/conversations/${activeConv.id}/messages`,
        { content: newMsg.trim() }
      );
      setMessages(prev => [...prev, data]);
      setNewMsg('');
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  // ── Search users (debounced 300ms) ──────────────────────────────────────────
  const handleSearchInput = (val) => {
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/users/search`, { params: { q: val.trim(), limit: 8 } });
        setSearchResults(data || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const handleOpenSearch = () => {
    setShowSearch(true);
    setSearchQuery('');
    setSearchResults([]);
    setTimeout(() => searchRef.current?.focus(), 80);
  };

  // ── Start or open DM with a user from search ────────────────────────────────
  const startDM = async (targetUser) => {
    setStartingDM(true);
    try {
      const { data } = await axios.post(`${API}/conversations/direct`, { user_id: targetUser.id });
      // Refresh list then open the conversation
      const params = activeTab !== 'all' ? { type: activeTab } : {};
      const listRes = await axios.get(`${API}/conversations`, { params });
      setConversations(listRes.data || []);
      await openConversation(data);
      setShowSearch(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setStartingDM(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const isDirect = (conv) => conv?.type === 'DIRECT';
  const kycOk = canDo(user, 'contact');

  return (
    <div>
      {/* Header */}
      <div className="c2s-topbar">
        <div className="c2s-topbar-title">Messages</div>
        <button
          data-testid="btn-new-dm"
          className="btn btn-primary btn-icon btn-sm"
          onClick={handleOpenSearch}
          title="Nouveau message direct"
          style={{ marginLeft: 'auto', marginRight: 4 }}
        >
          <Plus size={16} />
          <span style={{ fontSize: 12 }}>Nouveau</span>
        </button>
      </div>

      <div className="chat-wrap" data-testid="messaging-container">
        {/* ── Left panel : list + search ── */}
        <div className="chat-list" style={{ background: '#fff', boxShadow: 'var(--sh)', display: 'flex', flexDirection: 'column' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(15,23,42,.08)', flexShrink: 0 }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => { setActiveTab(tab.key); setActiveConv(null); }}
                style={{
                  flex: 1, padding: '9px 4px', border: 'none', background: 'none',
                  fontSize: 11.5, fontWeight: activeTab === tab.key ? 700 : 500,
                  color: activeTab === tab.key ? 'var(--teal)' : 'var(--ink2)',
                  borderBottom: activeTab === tab.key ? '2px solid var(--teal)' : '2px solid transparent',
                  cursor: 'pointer', transition: '.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* New DM — identité non vérifiée */}
          {showSearch && !kycOk && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15,23,42,.08)', background: 'var(--cream)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', flex: 1 }}>Nouveau message</span>
                <button
                  onClick={() => setShowSearch(false)}
                  aria-label="Fermer"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--ink3)' }}
                >
                  <X size={14} />
                </button>
              </div>
              <VerificationGate user={user} action="contact" variant="panel" />
            </div>
          )}

          {/* Search panel — new DM */}
          {showSearch && kycOk && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15,23,42,.08)', background: 'var(--cream)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', flex: 1 }}>Nouveau message</span>
                <button
                  onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--ink3)' }}
                >
                  <X size={14} />
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink3)', pointerEvents: 'none' }} />
                <input
                  ref={searchRef}
                  data-testid="input-dm-search"
                  type="text"
                  placeholder="Rechercher par prénom ou nom…"
                  value={searchQuery}
                  onChange={e => handleSearchInput(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '7px 10px 7px 28px',
                    border: '1.5px solid rgba(15,23,42,.15)', borderRadius: 8,
                    fontSize: 12.5, outline: 'none', background: '#fff',
                  }}
                />
              </div>

              {/* Search results */}
              {searching && (
                <div style={{ padding: '6px 2px', fontSize: 12, color: 'var(--ink3)' }}>Recherche…</div>
              )}
              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div style={{ padding: '6px 2px', fontSize: 12, color: 'var(--ink3)' }}>Aucun résultat</div>
              )}
              {searchResults.map(u => (
                <div
                  key={u.id}
                  data-testid={`search-result-${u.id}`}
                  onClick={() => !startingDM && startDM(u)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    padding: '7px 4px', cursor: startingDM ? 'wait' : 'pointer',
                    borderRadius: 6, marginTop: 4,
                    background: 'transparent', transition: '.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(15,23,42,.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div className="av sm av-teal" style={{ flexShrink: 0 }}>{getInitials(u)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      {u.roles?.includes('ROLE_CARRIER') && u.roles?.includes('ROLE_SENDER') ? 'Hybride'
                        : u.roles?.includes('ROLE_CARRIER') ? 'Transporteur'
                        : 'Expéditeur'}
                      {' '}<VerificationBadges user={u} compact />
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 600 }}>
                    {startingDM ? '…' : 'DM'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Conversation list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 16 }}>
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton" style={{ height: 58, marginBottom: 8, borderRadius: 8 }} />
                ))}
              </div>
            ) : conversations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--ink2)' }}>
                <MessageSquare size={30} style={{ opacity: .25, marginBottom: 8 }} />
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {activeTab === 'DIRECT' ? 'Aucun message direct' : activeTab === 'MISSION' ? 'Aucune conversation de mission' : 'Aucune conversation'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.5 }}>
                  {activeTab === 'DIRECT'
                    ? 'Cliquez sur « Nouveau » pour démarrer un DM.'
                    : 'Les conversations s\'ouvrent lors d\'une mission ou via le bouton Nouveau.'}
                </div>
              </div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.id}
                  data-testid={`conv-item-${conv.id}`}
                  className={`chat-item${activeConv?.id === conv.id ? ' active' : ''}`}
                  onClick={() => openConversation(conv)}
                >
                  <div className={`av sm ${isDirect(conv) ? 'av-violet' : 'av-teal'}`} style={{ flexShrink: 0 }}>
                    {getInitials(conv.other_user)}
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div className="chat-item-name">{conv.other_user?.first_name} {conv.other_user?.last_name}</div>
                        {isDirect(conv) && (
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 5px',
                            background: 'rgba(109,40,217,.1)', color: 'var(--violet, #6d28d9)',
                            borderRadius: 4, letterSpacing: '.4px',
                          }}>DM</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink3)', flexShrink: 0, marginLeft: 4 }}>
                        {conv.last_message_at ? formatDate(conv.last_message_at) : ''}
                      </div>
                    </div>
                    <div className="chat-item-last">{conv.last_message?.content || 'Aucun message'}</div>
                  </div>
                  {conv.unread_count > 0 && (
                    <span style={{
                      background: 'var(--red)', color: '#fff',
                      fontSize: 10, fontWeight: 700, padding: '2px 6px',
                      borderRadius: 10, flexShrink: 0, marginLeft: 4,
                    }}>
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Right panel : chat pane ── */}
        <div className="chat-pane" style={{ background: 'var(--cream)' }}>
          {!activeConv ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14, color: 'var(--ink3)', padding: 24 }}>
              <MessageSquare size={48} style={{ opacity: .15 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink2)' }}>Sélectionnez une conversation</div>
              <div style={{ fontSize: 13, color: 'var(--ink3)', textAlign: 'center', maxWidth: 240 }}>
                Ou démarrez un message direct via le bouton <strong>Nouveau</strong>.
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleOpenSearch} style={{ marginTop: 4 }}>
                <Plus size={14} /> Nouveau message
              </button>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid rgba(15,23,42,.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  onClick={() => setActiveConv(null)}
                  style={{ display: 'none' }}
                  data-testid="btn-back-chat"
                >
                  <ArrowLeft size={16} />
                </button>
                <div className={`av sm ${isDirect(activeConv) ? 'av-violet' : 'av-teal'}`}>
                  {getInitials(activeConv.other_user)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {activeConv.other_user?.first_name} {activeConv.other_user?.last_name}
                    </span>
                    {isDirect(activeConv) && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 6px',
                        background: 'rgba(109,40,217,.1)', color: 'var(--violet, #6d28d9)',
                        borderRadius: 4, letterSpacing: '.4px',
                      }}>DM</span>
                    )}
                  </div>
                  {activeConv.mission && (
                    <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      Mission · {activeConv.mission.status}
                    </div>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="chat-messages">
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--ink3)', fontSize: 13, padding: '28px 20px' }}>
                    Début de la conversation · dites bonjour !
                  </div>
                )}
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`msg ${msg.sender_id === user?.id ? 'mine' : 'other'}`}
                    data-testid={`msg-${msg.id}`}
                  >
                    <div className={`av sm ${msg.sender_id === user?.id ? 'av-navy' : isDirect(activeConv) ? 'av-violet' : 'av-teal'}`}>
                      {msg.sender_id === user?.id
                        ? (user.first_name || '?')[0].toUpperCase()
                        : getInitials(activeConv.other_user)}
                    </div>
                    <div>
                      <div className="msg-bubble">{msg.content}</div>
                      <div className="msg-time">{formatTime(msg.created_at)}</div>
                    </div>
                  </div>
                ))}
                <div ref={msgEndRef} />
              </div>

              {/* Input — conversation directe bloquée tant que l'identité n'est pas vérifiée */}
              {isDirect(activeConv) && !kycOk ? (
                <div style={{ padding: 12, borderTop: '1px solid rgba(15,23,42,.08)' }} data-testid="chat-kyc-required">
                  <VerificationGate user={user} action="contact" variant="panel" />
                </div>
              ) : (
              <form className="chat-input-wrap" onSubmit={sendMessage}>
                <input
                  data-testid="input-message"
                  type="text"
                  className="chat-input"
                  placeholder="Votre message…"
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  disabled={sending}
                  maxLength={2000}
                />
                <button
                  data-testid="btn-send-message"
                  type="submit"
                  className="btn btn-primary btn-icon"
                  disabled={sending || !newMsg.trim()}
                >
                  <Send size={16} />
                </button>
              </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
